import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { format, parse, isValid, startOfWeek, endOfWeek, isSameDay, startOfDay } from "date-fns";
import { Input } from "@/components/ui/input";
import { DateRange } from "@/hooks/useOrdersQuery";
import { cn } from "@/lib/utils";
import type { DateRange as DayPickerDateRange } from "react-day-picker";

type DatePreset = "today" | "this_week" | "custom";

function getPresetFromFilter(dateFilter: DateRange): DatePreset {
  if (!dateFilter) return "custom";
  const today = startOfDay(new Date());
  const weekStart = startOfWeek(today, { weekStartsOn: 0 });
  const weekEnd = endOfWeek(today, { weekStartsOn: 0 });
  if (isSameDay(dateFilter.from, today) && isSameDay(dateFilter.to, today)) return "today";
  if (isSameDay(dateFilter.from, weekStart) && isSameDay(dateFilter.to, weekEnd)) return "this_week";
  return "custom";
}

function getDateLabel(dateFilter: DateRange, defaultLabel: string): string {
  if (!dateFilter) return defaultLabel;
  const preset = getPresetFromFilter(dateFilter);
  if (preset === "today") return `Today, ${format(dateFilter.from, "MMM d")}`;
  if (preset === "this_week") return `This Week (${format(dateFilter.from, "MMM d")} – ${format(dateFilter.to, "MMM d")})`;
  if (isSameDay(dateFilter.from, dateFilter.to)) return format(dateFilter.from, "MMM d, yyyy");
  return `${format(dateFilter.from, "MMM d")} – ${format(dateFilter.to, "MMM d, yyyy")}`;
}

interface DateFilterPopoverProps {
  dateFilter: DateRange;
  onDateFilterChange: (d: DateRange) => void;
  /** Label shown when no filter is set (defaults to "Custom Range") */
  defaultLabel?: string;
  /** What "clear" resets to. Defaults to today/today; pass `null` to clear entirely. */
  clearTo?: DateRange | "today";
}

export function DateFilterPopover({
  dateFilter,
  onDateFilterChange,
  defaultLabel = "Custom Range",
  clearTo = "today",
}: DateFilterPopoverProps) {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState<DatePreset>(() => getPresetFromFilter(dateFilter));
  const [pendingRange, setPendingRange] = useState<DayPickerDateRange | undefined>(() =>
    dateFilter ? { from: dateFilter.from, to: dateFilter.to } : undefined
  );

  const handleOpen = (isOpen: boolean) => {
    if (isOpen) {
      setPreset(getPresetFromFilter(dateFilter));
      setPendingRange(dateFilter ? { from: dateFilter.from, to: dateFilter.to } : undefined);
    }
    setOpen(isOpen);
  };

  const apply = (range: DateRange) => {
    onDateFilterChange(range);
    setOpen(false);
  };

  const handlePresetClick = (p: DatePreset) => {
    setPreset(p);
    if (p === "today") {
      const today = startOfDay(new Date());
      apply({ from: today, to: today });
    } else if (p === "this_week") {
      const today = startOfDay(new Date());
      apply({ from: startOfWeek(today, { weekStartsOn: 0 }), to: endOfWeek(today, { weekStartsOn: 0 }) });
    }
  };

  const handleDone = () => {
    if (pendingRange?.from) {
      apply({ from: pendingRange.from, to: pendingRange.to ?? pendingRange.from });
    }
  };

  const resetTo: DateRange =
    clearTo === "today"
      ? (() => { const t = startOfDay(new Date()); return { from: t, to: t }; })()
      : clearTo;

  const isNotDefault =
    dateFilter !== null &&
    !(clearTo === "today" &&
      isSameDay(dateFilter.from, startOfDay(new Date())) &&
      isSameDay(dateFilter.to, startOfDay(new Date())));

  const label = getDateLabel(dateFilter, defaultLabel);

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <Button variant="default" size="sm">
          <CalendarIcon className="h-4 w-4 mr-1" />
          {label}
          {isNotDefault && (
            <span
              role="button"
              tabIndex={0}
              className="ml-1 rounded-full p-0.5 hover:bg-primary-foreground/20 transition-colors"
              onClick={(e) => { e.stopPropagation(); onDateFilterChange(resetTo); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onDateFilterChange(resetTo); } }}
            >
              <X className="h-3 w-3" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          <div className="border-r p-3 min-w-[140px] space-y-1">
            <p className="text-xs font-medium text-muted-foreground mb-2">Shortcuts</p>
            {([
              ["today", "Today"],
              ["this_week", "This Week"],
              ["custom", "Custom Range"],
            ] as [DatePreset, string][]).map(([key, lbl]) => (
              <button
                key={key}
                onClick={() => handlePresetClick(key)}
                className={cn(
                  "w-full text-left px-2 py-1.5 rounded text-sm transition-colors",
                  preset === key
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-accent text-foreground"
                )}
              >
                {lbl}
              </button>
            ))}
          </div>
          <div className="p-3 max-w-[280px]">
            {preset === "custom" && (
              <div className="flex gap-2 mb-2">
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground">Start</label>
                  <Input
                    className="h-8 text-sm"
                    placeholder="MM/DD/YYYY"
                    defaultValue={pendingRange?.from ? format(pendingRange.from, "MM/dd/yyyy") : ""}
                    key={pendingRange?.from?.toISOString() ?? "no-from"}
                    onBlur={(e) => {
                      const parsed = parse(e.target.value, "MM/dd/yyyy", new Date());
                      if (isValid(parsed)) {
                        setPendingRange((prev) => ({ from: startOfDay(parsed), to: prev?.to ?? startOfDay(parsed) }));
                      }
                    }}
                    onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground">End</label>
                  <Input
                    className="h-8 text-sm"
                    placeholder="MM/DD/YYYY"
                    defaultValue={pendingRange?.to ? format(pendingRange.to, "MM/dd/yyyy") : ""}
                    key={pendingRange?.to?.toISOString() ?? "no-to"}
                    onBlur={(e) => {
                      const parsed = parse(e.target.value, "MM/dd/yyyy", new Date());
                      if (isValid(parsed)) {
                        setPendingRange((prev) => ({ from: prev?.from ?? startOfDay(parsed), to: startOfDay(parsed) }));
                      }
                    }}
                    onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                  />
                </div>
              </div>
            )}
            <Calendar
              mode="range"
              selected={pendingRange}
              onSelect={(range) => {
                setPendingRange(range);
                if (preset !== "custom") setPreset("custom");
              }}
              className="p-0 pointer-events-auto"
            />
            {preset === "custom" && (
              <div className="flex justify-end gap-2 mt-2 pt-2 border-t">
                <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
                <Button size="sm" onClick={handleDone} disabled={!pendingRange?.from}>Done</Button>
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
